import React, { memo, useEffect, useRef, useState } from "react";
import styles from "./Sidebar.module.scss";
import { sideBarNavigation } from "@/lib/sidebarNavigation";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { checkPermission, clearOnLogout, combineClasses, getCurrentUser } from "@/lib/utils";
import { clear } from "@/store/slices/globalSlice";
import routes from "@/routes/routes";
import { supabase } from "@/services/supabase/client";
import LogoutIcon from "@/components/SVGComponents/LogoutIcon";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import ChangePasswordModal from "@/components/ChangePasswordModal/ChangePasswordModal";
import ConfirmLogoutModal from "@/components/ConfirmLogoutModal/ConfirmLogoutModal";
import ChangePasswordIcon from "@/components/SVGComponents/ChangePasswordIcon";
import type { RootState } from "@/store/rootReducer";
import type { AppDispatch } from "@/store/store";
import type { SidebarItem, SidebarChild } from "@/lib/sidebarNavigation";

// Define the navigation item type

// Define props
interface SidebarProps {
  toggleSidebarOpen: (open: boolean) => () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ toggleSidebarOpen }) => {
  const { sidebarOpen } = useSelector((state: RootState) => state.global);
  const [openDropdown, setOpenDropdown] = useState<string>("");

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const currentUser = getCurrentUser();
  const userName: string = currentUser?.userName || currentUser?.email || "";
  const userInitial = userName ? userName.charAt(0).toUpperCase() : "?";

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isUserMenuOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isUserMenuOpen]);

  const logout = async () => {
    await supabase.auth.signOut();
    clearOnLogout();
    dispatch(clear());
    navigate(routes?.publicRoutes?.LOGIN?.path ?? "/");
  };

  const toggleDropdown = (id: string) => () => {
    setOpenDropdown((prev) => (prev === id ? "" : id));
  };

  return (
    <div
      className={combineClasses(
        styles.leftSideContainer,
        !sidebarOpen ? styles.sidebarClosed : styles.sidebarOpen
      )}
      onMouseOver={toggleSidebarOpen(true)}
    >
      {/* Logo */}
      <div className={styles.logoContainer}>
        <div className={styles.iconContainer}>
          <div className={styles.mainIconDiv}>
            <img
              src="/Logo.png"
              alt="GD logo"
              className={styles.mainIconImage}
            />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.navContainer}>
        {sideBarNavigation
          .filter(
            (data: SidebarItem) =>
              checkPermission(data.permissionName) || data.children
          )
          .map((data: SidebarItem) => {
            const children = data?.children?.filter((child: SidebarChild) =>
              checkPermission(child.permissionName)
            );

            if (children?.length) {
              return (
                <React.Fragment key={data.id}>
                  <div
                    onClick={toggleDropdown(data.id)}
                    className={combineClasses(
                      styles.navLink,
                      !sidebarOpen && styles.sidebarClosed,
                      openDropdown === data.id && styles.activeLink
                    )}
                  >
                    <div className={styles.linksNames}>
                      {data.icon && (
                        <data.icon className={styles.icon} />
                      )}
                      {sidebarOpen && (
                        <p className={styles.linkPageName}>{data.pageName}</p>
                      )}
                    </div>
                    <ArrowIcon
                      className={styles.arrowIcon}
                      style={{
                        transform:
                          openDropdown === data.id
                            ? "rotate(-90deg)"
                            : "rotate(90deg)",
                        transition: "transform ease 300ms",
                      }}
                    />
                  </div>

                  {openDropdown === data.id && (
                    <div className={styles.leftBorder}>
                      {children.map((child: SidebarChild) => (
                        <NavLink
                          key={child.id}
                          className={({ isActive }) =>
                            `${styles.navLink} ${styles.childNavLink} ${
                              !sidebarOpen ? styles.sidebarClosed : ""
                            } ${isActive && styles.activeLink}`
                          }
                          to={`${data.path}/${child.path}`}
                          state={{ previousPath: location.pathname }}
                        >
                          <div className={styles.linksNames}>
                            {child.icon && (
                              <child.icon className={styles.icon} />
                            )}
                            {sidebarOpen && (
                              <p className={styles.linkPageName}>
                                {child.pageName}
                              </p>
                            )}
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            } else if (!children) {
              return (
                <NavLink
                  key={data.id}
                  className={({ isActive }) =>
                    `${styles.navLink} ${
                      !sidebarOpen ? styles.sidebarClosed : ""
                    } ${isActive && styles.activeLink}`
                  }
                  to={data.path}
                  state={{ previousPath: location.pathname }}
                  onClick={toggleDropdown("")}
                >
                  <div className={styles.linksNames}>
                    {data.icon && <data.icon className={styles.icon} />}
                    {sidebarOpen && (
                      <p className={styles.linkPageName}>{data.pageName}</p>
                    )}
                  </div>
                </NavLink>
              );
            }
            return null;
          })}
      </div>

      {/* Current logged-in user */}
      <div className={styles.userInfoWrapper} ref={userMenuRef}>
        <div
          className={`${styles.userInfo} ${
            !sidebarOpen ? styles.sidebarClosed : ""
          }`}
          onClick={() => setIsUserMenuOpen((prev) => !prev)}
        >
          <span className={styles.userAvatar}>{userInitial}</span>
          {sidebarOpen && (
            <div className={styles.userDetails}>
              <p className={styles.userName} title={userName}>
                {userName || "—"}
              </p>
              {currentUser?.email && (
                <p className={styles.userEmail} title={currentUser.email}>
                  {currentUser.email}
                </p>
              )}
            </div>
          )}
        </div>

        {isUserMenuOpen && (
          <div className={styles.userMenu}>
            <button
              type="button"
              className={styles.userMenuItem}
              onClick={() => {
                setIsUserMenuOpen(false);
                setIsChangePasswordOpen(true);
              }}
            >
              <ChangePasswordIcon className={styles.userMenuIcon} />
              Change Password
            </button>
            <button
              type="button"
              className={styles.userMenuItem}
              onClick={() => {
                setIsUserMenuOpen(false);
                setIsLogoutConfirmOpen(true);
              }}
            >
              <LogoutIcon className={combineClasses(styles.userMenuIcon, styles.logoutMenuIcon)} />
              Logout
            </button>
          </div>
        )}
      </div>

      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        email={currentUser?.email ?? ""}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <ConfirmLogoutModal
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={() => {
          setIsLogoutConfirmOpen(false);
          logout();
        }}
      />
    </div>
  );
};

export default memo(Sidebar);
